"use client";
import { AnimatePresence, motion } from "framer-motion";
import { ComponentType, useEffect, useState } from "react";
import {
  BabyFace,
  Balloon,
  Bed,
  Book,
  Bottle,
  Bus,
  Cake,
  Car,
  Cat,
  Confetti,
  Dog,
  Gift,
  Happy,
  Heart,
  Horse,
  Letter,
  Moon,
  Paw,
  Rainbow,
  Road,
  Sad,
  Shoe,
  Silence,
  Sleep,
  Stroller,
  Sun,
} from "./HeroIcons";
import LetterReveal from "./LetterReveal";

import Box from "@mui/material/Box";

export const ROTATE_INTERVAL = 4000; // ms

interface IconInstance {
  id: string;
  Component: ComponentType;
  leftPct: number;
  topPct: number;
  scale: number;
  exitX: number;
  exitY: number;
  opacity: number;
  rotateDeg: number;
  wiggle: number;
}

// Map words to the set of icon components we want to show for them.
const WORD_ICON_MAP: Record<string, ComponentType[]> = {
  "copilul tău": [BabyFace, Bottle, Horse],
  "zile de naștere": [Balloon, Cake, Gift, Confetti],
  "primii pași": [Stroller, Paw, Shoe],
  "învățat alfabetul": [Book, Letter],
  "seri de somn": [Moon, Sleep, Bed],
  "drumuri lungi": [Car, Bus, Road],
  botezuri: [Balloon, Confetti],
  "amintiri de neuitat": [Rainbow],
  "povești de seară": [Moon, Book, Sleep, Bed],
  "culorile curcubeului": [Rainbow, Sun],
  "gestionarea emoțiilor": [Heart, Silence, Sad, Happy],
  "descoperit animale": [Dog, Cat, Paw, Horse],
  "petreceri cu prietenii": [Balloon, Cake, Gift, Heart, Confetti],
};

// derive the words list from the dictionary keys so we don't repeat phrases
let words = Object.keys(WORD_ICON_MAP);

//randomize words so we don't always start with the same one, but keep first one
const first = words[0];
words = [first, ...words.slice(1).sort(() => Math.random() - 0.5)];

const longest = words.reduce(
  (a, b) => (a.length >= b.length ? a : b),
  words[0],
);

export default function RotatingWord({
  externalIndex,
}: {
  externalIndex?: number;
}) {
  const [internalIndex, setInternalIndex] = useState(0);
  const [iconInstances, setIconInstances] = useState<IconInstance[]>([]);

  useEffect(() => {
    if (externalIndex !== undefined) return;
    // Hold each word longer before rotating to the next one.
    const id = setInterval(() => {
      setInternalIndex((i) => i + 1);
    }, ROTATE_INTERVAL);

    return () => clearInterval(id);
  }, [externalIndex]);

  const index = externalIndex ?? internalIndex;
  const word = words[index % words.length];

  function seededRandom(seed: number) {
    // simple deterministic PRNG
    let v = seed % 2147483647;
    if (v <= 0) v += 2147483646;
    return () => (v = (v * 16807) % 2147483647) / 2147483647;
  }

  function generateIconsForWord(w: string, idx: number): IconInstance[] {
    const seed = idx + w.length;
    const rand = seededRandom(seed);
    const components = WORD_ICON_MAP[w] ?? [BabyFace, Bottle, Rainbow];
    const out: IconInstance[] = [];
    // If there are multiple icons, distribute them roughly uniformly using
    // a deterministic grid with a small seeded jitter so they don't cluster.
    const N = components.length;
    const rows = Math.ceil(Math.sqrt(N));
    const cols = Math.ceil(N / rows);

    const cellWidthPct = 100 / cols;
    const cellHeightPct = 100 / rows;

    // size scaling based on number of icons so they don't overlap too much
    const sizeScaleFactor = Math.max(0.6, 1 - (N - 1) * 0.07);

    for (let i = 0; i < N; i++) {
      const Component = components[i];

      const row = Math.floor(i / cols);
      const col = i % cols;

      const baseLeft = (col + 0.5) * cellWidthPct;
      const baseTop = (row + 0.5) * cellHeightPct;

      // jitter up to +/-20% of cell size for a natural look
      const jitterX = (rand() - 0.5) * cellWidthPct * 0.4;
      const jitterY = (rand() - 0.5) * cellHeightPct * 0.4;

      let leftPct = baseLeft + jitterX;
      let topPct = baseTop + jitterY;

      // clamp to viewport margins (allow icons much closer to edges)
      leftPct = Math.min(99, Math.max(1, leftPct));
      topPct = Math.min(99, Math.max(1, topPct));

      // push icons away from the center so they avoid the middle of the hero
      const dx = leftPct - 50;
      const dy = topPct - 50;
      let dist = Math.sqrt(dx * dx + dy * dy);
      const minDist = 20; // percent radius to avoid around center
      const minAxisDist = 14; // percent min distance from vertical/horizontal center lines

      // If within the central circle OR too close to the center axes, push outward.
      if (
        dist < minDist ||
        Math.abs(dx) < minAxisDist ||
        Math.abs(dy) < minAxisDist
      ) {
        let angle = Math.atan2(dy, dx);
        if (dist === 0) angle = rand() * Math.PI * 2;

        // compute required push to escape both radial and axis constraints
        const needRadial = Math.max(0, minDist - dist);
        const needAxisX = Math.max(0, minAxisDist - Math.abs(dx));
        const needAxisY = Math.max(0, minAxisDist - Math.abs(dy));
        const axisNeed = Math.max(needAxisX, needAxisY);
        const push = Math.max(needRadial, axisNeed) + rand() * 6; // add jitter

        const newDist = Math.max(dist + push, minDist + rand() * 2);
        leftPct = 50 + Math.cos(angle) * newDist;
        topPct = 50 + Math.sin(angle) * newDist;

        // re-clamp after pushing
        leftPct = Math.min(99, Math.max(1, leftPct));
        topPct = Math.min(99, Math.max(1, topPct));
      }

      const size = Math.round((90 + rand() * 100) * sizeScaleFactor);
      const BASE_ICON_SIZE = 100;
      const scale = size / BASE_ICON_SIZE;

      const exitX = ((leftPct - 50) / 50) * 120;
      const exitY = ((topPct - 50) / 50) * 120;
      const opacity = 0.2 + rand() * 0.12;
      const rotateDeg = Math.round(rand() * 50 - 25);
      const wiggle = Math.round(rand() * 10 - 5);

      out.push({
        id: `${idx}-${i}`,
        Component,
        leftPct,
        topPct,
        scale,
        exitX,
        exitY,
        opacity,
        rotateDeg,
        wiggle,
      });
    }
    return out;
  }

  useEffect(() => {
    setIconInstances(generateIconsForWord(word, index));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, word]);

  return (
    <Box>
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
          overflow: "visible",
          zIndex: 0
        }}>
        <AnimatePresence>
          {iconInstances.map((it, idx) => {
            const {
              id,
              Component,
              leftPct,
              topPct,
              scale,
              exitX,
              exitY,
              opacity,
              rotateDeg,
              wiggle,
            } = it;
            return (
              <Box
                key={id}
                sx={{
                  position: "absolute",
                  left: `${leftPct}%`,
                  top: `${topPct}%`,
                  pointerEvents: "none",
                  zIndex: 0,
                  transform: "translate(-50%, -50%)",
                  color: "primary.main",
                }}
              >
                <motion.div
                  initial={{
                    opacity: 0,
                    scale: 0.6 * scale,
                    rotate: rotateDeg,
                  }}
                  animate={{
                    opacity: opacity,
                    scale: 1 * scale,
                    rotate: [rotateDeg, rotateDeg + wiggle, rotateDeg],
                  }}
                  exit={{
                    opacity: 0,
                    // make exit scale closer to the current size for a subtler shrink
                    scale: 0.92 * scale,
                    // reduce how far icons fly away on exit
                    x: exitX * 0.35,
                    y: exitY * 0.35,
                    // avoid dramatic spinning on exit
                    rotate: rotateDeg + 10,
                    transition: { duration: 0.6, ease: "easeOut" },
                  }}
                >
                  <Component />
                </motion.div>
              </Box>
            );
          })}
        </AnimatePresence>
      </Box>
      <Box
        sx={{
          position: "relative",
          display: "inline-block",
          textAlign: "center",
          maxWidth: { md: "26ch" },
          lineHeight: 1.15,
        }}
      >
        <AnimatePresence mode={"wait"}>
          <motion.span
            key={word}
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{
              opacity: 0,
              filter: "blur(6px)",
              transition: { duration: 0.35 },
            }}
            style={{
              position: "absolute",
              left: "50%",
              top: 0,
              transform: "translateX(-50%)",
              display: "inline-block",
              whiteSpace: "pre-wrap",
              width: "100%",
              textAlign: "center",
              lineHeight: 1.15,
            }}
          >
            <LetterReveal text={word} instant={index === 0} />
          </motion.span>
        </AnimatePresence>
        {/* Reserve space using the longest phrase so we don't clip or shift */}
        <span
          aria-hidden
          style={{
            visibility: "hidden",
            display: "block",
            whiteSpace: "pre-wrap",
            width: "100%",
            lineHeight: 1.15,
          }}
        >
          {longest}
        </span>
      </Box>
    </Box>
  );
}
